import React, {
  FunctionComponent,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Noun, Verb } from '@trello/analytics';
import { Source, ActionSubjectId } from '@trello/atlassian-analytics';
import { N800 } from '@trello/colors';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { useEmailHygieneWizardQuery } from './EmailHygieneWizardQuery.generated';
import { currentLocale } from '@trello/locale';
import { idToDate, dateDifferenceInDays } from '@trello/dates';
import { TrelloStorage } from '@trello/storage';
import Banner from 'app/common/components/banner/banner';
import { forNamespace, localizeCount } from 'app/common/lib/util/i18n';
import {
  Overlay,
  OverlayAlignment,
  OverlayBackground,
} from 'app/src/components/Overlay';
import { track, trackScreenEvent } from './Analytics';
import styles from './EmailHygieneBanner.less';
import { CHANGE_EMAIL_MESSAGE_ID } from './emailHygieneOneTimeMessageIds';
import { useSnooze } from './useSnooze';
import { useLazyComponent } from '@trello/use-lazy-component';

const deadline = new Date(2020, 5, 1);

let analyticsContext: object;

const format = forNamespace(['email hygiene wizard', 'banner'], {
  shouldEscapeStrings: false,
});

export const forceEmailHygieneQuery = /[?&]forceHygiene=true/i;

export interface EmailHygieneBannerProps {
  forceShow?: boolean;
}

export const EmailHygieneBanner: FunctionComponent<EmailHygieneBannerProps> = ({
  forceShow,
}) => {
  const EmailHygieneWizard = useLazyComponent(() =>
    import(
      /* webpackChunkName: "email-hygiene-wizard" */ './EmailHygieneWizard'
    ),
  );
  const [shouldShowHygiene, setShouldShowHygiene] = useState(false);
  const [isOverlayOpen, setOverlayOpen] = useState(false);
  const [seenInitialBanner, setSeenInitialBanner] = useState(false);

  const isTargeted = useFeatureFlag('aa4a.show-email-hygiene', false);
  const useCountdownBanner = useFeatureFlag('aa4a.show-peh-countdown', false);
  const usePastDeadlineBanner = useFeatureFlag(
    'aa4a.show-peh-past-deadline',
    false,
  );
  const multipleEmailsUndismissable = useFeatureFlag(
    'aa4a.peh-undismissable-for-multiple-emails',
    false,
  );

  const snoozeSeconds = useFeatureFlag('aa4a.peh-snooze-time', 0);
  const { isSnoozed, snooze } = useSnooze('peh-snooze', snoozeSeconds);

  const dataHook = useEmailHygieneWizardQuery({
    variables: { memberId: 'me' },
  });

  const me = dataHook.data && dataHook.data.member;

  const pastDeadline = new Date() > deadline;
  const showCountdown =
    seenInitialBanner && useCountdownBanner && !pastDeadline;

  useEffect(() => {
    if (!me?.logins) {
      return;
    }

    const didChangeEmail = !!me.oneTimeMessagesDismissed?.includes(
      CHANGE_EMAIL_MESSAGE_ID,
    );
    const oldestLogin = me.logins.sort((a, b) => a.id.localeCompare(b.id))[0];
    const timeDiffInDays = oldestLogin
      ? (Date.now() - idToDate(oldestLogin.id).valueOf()) / 1000 / 60 / 60 / 24
      : 0;

    /**
     * These conditionals are a little complicated, and here's why:
     * We need to cover a few different cases, where a user may become
     * un-targeted while going through the steps in the PEH wizard.
     * For example, if they are being targeted because they fall into a
     * claimable email cohort, and we show them the wizard, then, in the
     * wizard, they change their email to a un-claimable email they might
     * fall out of the "claimable email cohort" and no
     * longer be `isTargeted: true`. In this case, we do not want to have the
     * wizard and banner be hidden, due to this change of email.
     */

    if (isSnoozed) {
      setOverlayOpen(false);
      setShouldShowHygiene(false);
    } else if (forceShow) {
      setShouldShowHygiene(true);
      setOverlayOpen(true);
    } else if (timeDiffInDays <= 7 && !didChangeEmail) {
      setShouldShowHygiene(false);
      // Don't hide the wizard
    } else if (didChangeEmail || isTargeted) {
      setShouldShowHygiene(true);
      setOverlayOpen(true);
    }

    if (TrelloStorage.get('peh-seen-initial-banner')) {
      setSeenInitialBanner(true);
    }
  }, [me, forceShow, isTargeted, isSnoozed]);

  const onClickConfirmEmail = useCallback(() => {
    dataHook.refetch();
    setOverlayOpen(true);
    track({
      verb: Verb.INITIATES,
      noun: Noun.EMAIL_HYGIENE_WIZARD,
      method: 'by clicking banner link',
      context: analyticsContext,
      source: Source.emailHygieneBanner,
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: ActionSubjectId.emailHygieneBannerLink,
    });
  }, [dataHook]);

  const onClickSnoozeLink = useCallback(() => {
    snooze();
    track({
      verb: Verb.DISMISSES,
      noun: Noun.EMAIL_HYGIENE_WIZARD,
      method: 'by clicking remind me later link',
      context: analyticsContext,
      source: Source.emailHygieneBanner,
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: ActionSubjectId.emailHygieneBannerDismissLink,
    });
  }, [snooze]);

  const logins = me && Array.from(me.logins);
  const isUndismissable =
    multipleEmailsUndismissable && !!logins && logins.length > 1;

  // sort by two criteria:
  //   1. primary (to make sure that the current primary email address is on top)
  //   2. ID desc (so that the most recently added addresses will be the highest)
  if (logins) {
    logins.sort(
      (a, b) =>
        (b.primary ? 1 : 0) - (a.primary ? 1 : 0) || b.id.localeCompare(a.id),
    );
  }

  if (!analyticsContext && !!me) {
    analyticsContext = {
      organizationId: null,
      totalEmailAddresses: logins?.length,
      totalClaimableEmailAddresses: logins?.filter((l) => l.claimable).length,
    };
  }

  const renderButtons = () => {
    return (
      <>
        <a
          href="#"
          className={styles.updateLink}
          onClick={(e) => {
            e.preventDefault();
            onClickConfirmEmail();
          }}
        >
          {format('link-text')}
        </a>
        {!isUndismissable && snoozeSeconds > 0 && (
          <>
            {/* this symbol is the little dot that separates the links */}
            <span className={styles.linkSeparator}>&#183;</span>
            <a
              href="#"
              className={styles.dismissLink}
              onClick={(e) => {
                e.preventDefault();
                onClickSnoozeLink();
              }}
            >
              {format('snooze-link-text')}
            </a>
          </>
        )}
      </>
    );
  };

  const initialBanner = () => {
    TrelloStorage.set('peh-seen-initial-banner', 'true');
    return (
      <Banner className={styles.banner} bannerColor={N800}>
        <p className={styles.text}>
          <span role="img" aria-label="">
            👋
          </span>
          {format('main-message', { name: me && me.fullName })}
          {renderButtons()}
        </p>
      </Banner>
    );
  };

  const countdownBanner = () => {
    const daysLeft = Math.ceil(
      dateDifferenceInDays(new Date(), deadline, true),
    );
    return (
      <Banner className={styles.banner} bannerColor={N800}>
        <p className={styles.text}>
          <strong>
            {localizeCount('peh-countdown-banner-days', daysLeft, {
              days: daysLeft,
            })}
          </strong>
          <br />
          {format('countdown-message', {
            date: deadline.toLocaleDateString(currentLocale, {
              month: 'long',
              day: 'numeric',
            }),
          })}
          {renderButtons()}
        </p>
      </Banner>
    );
  };

  const pastDeadlineBanner = () => {
    return (
      <Banner className={styles.banner} bannerColor={N800}>
        <p className={styles.text}>
          {format('past-deadline-message')}
          {renderButtons()}
        </p>
      </Banner>
    );
  };

  useEffect(() => {
    if (shouldShowHygiene) {
      trackScreenEvent(Source.emailHygieneBanner, analyticsContext);
    }
  }, [shouldShowHygiene]);

  return !me || !logins ? null : (
    <div>
      {shouldShowHygiene && (
        <>
          {pastDeadline && usePastDeadlineBanner
            ? pastDeadlineBanner()
            : showCountdown
            ? countdownBanner()
            : initialBanner()}
        </>
      )}
      {isOverlayOpen && (
        <Overlay
          background={OverlayBackground.LIGHT}
          closeOnEscape={false}
          onClose={() => {}}
          alignment={OverlayAlignment.TOP}
        >
          <Suspense fallback={null}>
            <EmailHygieneWizard
              logins={logins}
              name={me.fullName || me.username}
              hasTwoFactor={!!me.prefs?.twoFactor?.enabled}
              avatarUrl={me.avatarUrl || undefined}
              initials={me.initials || undefined}
              onDismiss={() => setOverlayOpen(false)}
              isUndismissable={isUndismissable}
              onSnooze={snoozeSeconds ? snooze : undefined}
              analyticsContext={analyticsContext}
              onComplete={() => {
                setShouldShowHygiene(false);
                setOverlayOpen(false);
              }}
            />
          </Suspense>
        </Overlay>
      )}
    </div>
  );
};
